import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import {
  apiClient,
  type DepartmentDirectoryItem,
  type DepartmentFilters,
  type DepartmentSummary,
} from "../api/client";
import { useAuth } from "../app/AuthContext";
import { errorMessage } from "./admin/errors";
import { AddDepartmentDialog } from "./departments/AddDepartmentDialog";
import { DepartmentDetailDrawer } from "./departments/DepartmentDetailDrawer";

export function DepartmentManagementPage() {
  const { session } = useAuth();
  const [departments, setDepartments] = useState<DepartmentDirectoryItem[]>([]);
  const [filters, setFilters] = useState<DepartmentFilters>({});
  const [draft, setDraft] = useState<DepartmentFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<DepartmentDirectoryItem | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const detailTrigger = useRef<HTMLButtonElement | null>(null);
  const restoreDetailFocusPending = useRef(false);
  const requestSequence = useRef(0);

  const loadDepartments = useCallback(async (selectedFilters: DepartmentFilters) => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError("");
    try {
      const next = await apiClient.listDepartments(selectedFilters);
      if (requestId !== requestSequence.current) return null;
      setDepartments(next);
      return next;
    } catch (caught) {
      if (requestId === requestSequence.current) setError(errorMessage(caught));
      throw caught;
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDepartments({}).catch(() => undefined);
  }, [loadDepartments]);

  useEffect(() => {
    if (!selected && restoreDetailFocusPending.current) {
      restoreDetailFocusPending.current = false;
      const trigger = detailTrigger.current;
      if (trigger?.isConnected) trigger.focus();
      else addButton.current?.focus();
    }
  }, [selected]);

  if (session?.role !== "owner") {
    return (
      <section className="empty-page">
        <span className="eyebrow">权限不足</span>
        <h1>部门管理</h1>
        <p>只有完成 MFA 的 owner 可以查看和维护全局部门目录。</p>
      </section>
    );
  }

  function submitFilters(event: FormEvent) {
    event.preventDefault();
    const selectedFilters = {
      search: draft.search?.trim() || undefined,
      status: draft.status,
    };
    setFilters(selectedFilters);
    void loadDepartments(selectedFilters).catch(() => undefined);
  }

  function clearFilters() {
    const defaults: DepartmentFilters = {};
    setDraft(defaults);
    setFilters(defaults);
    void loadDepartments(defaults).catch(() => undefined);
  }

  function closeCreate() {
    setCreateOpen(false);
    queueMicrotask(() => addButton.current?.focus());
  }

  function closeDetail() {
    restoreDetailFocusPending.current = true;
    setSelected(null);
  }

  async function created(department: DepartmentSummary) {
    const defaults: DepartmentFilters = {};
    setDraft(defaults);
    setFilters(defaults);
    try {
      await loadDepartments(defaults);
      setMessage(`已创建 ${department.name}，并刷新“已创建部门”列表。`);
    } catch {
      setMessage(`已创建 ${department.name}，但目录刷新失败，请重试。`);
    }
    closeCreate();
  }

  async function changed(department: DepartmentSummary) {
    setSelected((current) =>
      current
        ? {
            ...current,
            ...department,
            is_active: department.is_active ?? current.is_active,
            status: department.status ?? current.status,
          }
        : current,
    );
    try {
      const next = await loadDepartments(filters);
      const refreshed = next?.find((item) => item.id === department.id) ?? null;
      if (!refreshed) restoreDetailFocusPending.current = true;
      setSelected(refreshed);
      setMessage(`${department.name} 的部门目录已刷新。`);
    } catch {
      setMessage(`${department.name} 已更新，但目录刷新失败，请重试。`);
    }
  }

  const filtered = Boolean(filters.search || filters.status);

  return (
    <div className="page-stack department-management-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">组织范围权威目录</span>
          <h1>部门管理</h1>
          <p>维护稳定部门身份；停用保留历史归因，并清理仍活动的授权与配额。</p>
        </div>
        <button
          ref={addButton}
          className="primary-link"
          onClick={() => {
            setMessage("");
            setSelected(null);
            setCreateOpen(true);
          }}
          type="button"
        >
          新增部门
        </button>
      </header>

      <section className="flow-panel department-directory-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">全部生命周期状态</span>
            <h2>已创建部门</h2>
          </div>
          <span className="phase-badge">{departments.length} 个结果</span>
        </div>
        <form className="department-directory-filters" onSubmit={submitFilters}>
          <label>
            搜索
            <input
              onChange={(event) => setDraft({ ...draft, search: event.target.value })}
              placeholder="部门名称或编码"
              type="search"
              value={draft.search ?? ""}
            />
          </label>
          <label>
            状态
            <select
              onChange={(event) =>
                setDraft({
                  ...draft,
                  status: (event.target.value || undefined) as DepartmentFilters["status"],
                })
              }
              value={draft.status ?? ""}
            >
              <option value="">全部状态</option>
              <option value="active">活动</option>
              <option value="inactive">已停用</option>
            </select>
          </label>
          <div className="user-filter-actions">
            <button type="submit">应用筛选</button>
            <button
              className="secondary-button"
              onClick={() => {
                clearFilters();
              }}
              type="button"
            >
              清除
            </button>
          </div>
        </form>
        {message && (
          <p className="status-banner" role="status">
            {message}
          </p>
        )}
        {error && (
          <p className="alert error" role="alert">
            {error}
          </p>
        )}
        <DepartmentDirectory
          departments={departments}
          failed={Boolean(error)}
          filtered={filtered}
          loading={loading}
          onAdd={() => {
            setMessage("");
            setCreateOpen(true);
          }}
          onClear={clearFilters}
          onSelect={(department, trigger) => {
            detailTrigger.current = trigger;
            setCreateOpen(false);
            setSelected(department);
          }}
        />
      </section>
      {createOpen && <AddDepartmentDialog onClose={closeCreate} onCreated={created} />}
      {selected && (
        <DepartmentDetailDrawer department={selected} onChanged={changed} onClose={closeDetail} />
      )}
    </div>
  );
}

function DepartmentDirectory({
  departments,
  failed,
  filtered,
  loading,
  onAdd,
  onClear,
  onSelect,
}: {
  departments: DepartmentDirectoryItem[];
  failed: boolean;
  filtered: boolean;
  loading: boolean;
  onAdd: () => void;
  onClear: () => void;
  onSelect: (department: DepartmentDirectoryItem, trigger: HTMLButtonElement) => void;
}) {
  if (loading) return <p className="panel-copy">正在读取部门目录…</p>;
  if (failed && !departments.length)
    return (
      <div className="department-empty-state">
        <h3>暂时无法读取部门目录</h3>
        <p>请检查网络或稍后重试；系统尚未确认当前是否已创建部门。</p>
      </div>
    );
  if (filtered && !departments.length)
    return (
      <div className="department-empty-state">
        <h3>没有符合筛选条件的部门</h3>
        <p>调整名称、编码或状态筛选后重试。</p>
        <button onClick={onClear} type="button">
          清除筛选
        </button>
      </div>
    );
  if (!departments.length)
    return (
      <div className="department-empty-state">
        <h3>尚未创建部门</h3>
        <p>创建首个部门后，即可用于用户归属、授权、配额和业务运行范围。</p>
        <button onClick={onAdd} type="button">
          新增部门
        </button>
      </div>
    );
  return (
    <div className="user-directory-table-wrap">
      <table className="user-directory-table department-directory-table">
        <thead>
          <tr>
            <th>部门名称</th>
            <th>编码</th>
            <th>状态</th>
            <th>活动用户数</th>
            <th>活动授权数</th>
            <th>活动配额数</th>
          </tr>
        </thead>
        <tbody>
          {departments.map((department) => (
            <tr key={department.id}>
              <td>
                <button
                  className="department-row-button"
                  onClick={(event) => onSelect(department, event.currentTarget)}
                  type="button"
                >
                  {department.name}
                </button>
              </td>
              <td>{department.code}</td>
              <td>
                <span className={`account-state ${department.status}`}>
                  {department.status === "active" ? "活动" : "已停用"}
                </span>
              </td>
              <td>{department.active_user_count}</td>
              <td>{department.active_grant_count}</td>
              <td>{department.active_quota_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
